import React, { useCallback, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  type LayoutChangeEvent,
  StyleSheet,
  View,
  type ViewStyle,
} from 'react-native';
import Animated from 'react-native-reanimated';
import { useStyle } from 'react-native-style-utilities';
import { theme } from '@garnish/constants';
import { Container, useResponsive } from '@sg/garnish';

import { LoyaltyHomeProgressBarMilestone } from './components';
import { useLoyaltyHomeProgressBarAnimation } from './hooks';

export const LoyaltyHomeProgressBar = (props: LoyaltyHomeProgressBarProps) => {
  const {
    availablePoints,
    totalPoints,
    pointsRewards,
    palette,
    size = 'medium',
    compact,
    dataSet,
    isAnimated = true,
  } = props;
  const { formatMessage } = useIntl();

  const { match } = useResponsive();

  const containerSpacing = match([
    theme.spacing['6'],
    theme.spacing['10'],
    theme.spacing['30'],
  ]);

  const barSize = match([styles.barSizeXS, styles.barSizeSM]);

  const progressBarColorBase = palettesBase[palette];
  const progressBarColorPending = palettesPending[palette];
  const progressBarColorAvailable = palettesAvailable[palette];

  const [progressBarWidth, setProgressBarWidth] = useState(0);
  const fullBarStyle = useStyle(
    () => ({
      width: progressBarWidth,
      opacity: progressBarWidth > 0 ? 1 : 0,
    }),
    [progressBarWidth],
  );

  const progress = useMemo(() => {
    const lastRewardPoints = pointsRewards.at(-1)?.points ?? 0;

    return Math.round((availablePoints / lastRewardPoints) * 100);
  }, [availablePoints, pointsRewards]);

  const { availableBarStyle, pendingBarStyle } =
    useLoyaltyHomeProgressBarAnimation({
      progressBarWidth,
      availablePoints,
      totalPoints,
      pointsRewards,
      isAnimated,
    });

  const onLayout = useCallback((event: LayoutChangeEvent) => {
    setProgressBarWidth(event.nativeEvent.layout.width);
  }, []);

  return (
    <Container
      customSpacing={containerSpacing}
      accessibilityRole="progressbar"
      accessibilityLabel={formatMessage(messages.progressBar, { progress })}
      accessibilityValue={{ min: 0, max: 100, now: progress }}
      {...(dataSet ? { dataSet: dataSet } : {})}
    >
      <View>
        <Animated.View
          style={[
            styles.pendingBar,
            barSize,
            progressBarColorPending,
            pendingBarStyle,
          ]}
        />

        <Animated.View
          style={[
            styles.availableBar,
            barSize,
            progressBarColorAvailable,
            availableBarStyle,
          ]}
        />

        <View
          style={[styles.progressBar, barSize, progressBarColorBase]}
          onLayout={onLayout}
        />

        <View style={[styles.darkThemeBorder, barSize, fullBarStyle]} />

        <View style={styles.milestonesContainer}>
          <LoyaltyHomeProgressBarMilestone
            palette={palette}
            availablePoints={availablePoints}
            isFluid={!compact}
            size={size}
          />

          {pointsRewards.map((pointsReward, index) => (
            <LoyaltyHomeProgressBarMilestone
              key={pointsReward.id}
              palette={palette}
              availablePoints={availablePoints}
              pointsReward={pointsReward}
              size={size}
              isFluid={true}
              index={index}
            />
          ))}
        </View>
      </View>
    </Container>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  progressBar: {
    defaultMessage: `Loyalty reward progress bar: {progress}%`,
    description: 'Loyalty Home | Progress Bar | A11y',
  },
});

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  barSizeXS: {
    minHeight: 16,
    borderRadius: 16,
  },
  barSizeSM: {
    minHeight: 40,
    borderRadius: 40,
  },
  progressBar: {
    zIndex: 1,
    flex: 1,
    flexDirection: 'row',
  },
  pendingBar: {
    zIndex: 2,
    position: 'absolute',
  },
  availableBar: {
    zIndex: 3,
    position: 'absolute',
  },
  milestonesContainer: {
    zIndex: 4,
    flexDirection: 'row',
    gap: theme.spacing['3'],
  },
  darkThemeBorder: {
    position: 'absolute',
    borderColor: theme.colors.CREAM,
    borderWidth: 1,
    zIndex: 5,
  },
});

// ─── Palettes ───────────────────────────────────────────────────────────────

const palettesBase: Record<LoyaltyHomeProgressBarProps['palette'], ViewStyle> =
  {
    dark: {
      backgroundColor: theme.colors.BASIL,
    },
    light: {
      backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
    },
  };

const palettesPending: Record<
  LoyaltyHomeProgressBarProps['palette'],
  ViewStyle
> = {
  dark: {
    backgroundColor: theme.colors.CREAM,
  },
  light: {
    backgroundColor: theme.colors.AVOCADO,
  },
};

const palettesAvailable: Record<
  LoyaltyHomeProgressBarProps['palette'],
  ViewStyle
> = {
  dark: {
    backgroundColor: theme.colors.AVOCADO,
  },
  light: {
    backgroundColor: theme.colors.DARK_BASIL,
  },
};

// ─── Types ──────────────────────────────────────────────────────────────────

type LoyaltyHomeProgressBarProps = {
  availablePoints: number;
  totalPoints: number;
  pointsRewards: readonly PointsReward[];
  palette: 'dark' | 'light';
  size?: 'medium' | 'large';
  isAnimated?: boolean;
  compact?: boolean;
  dataSet?: Record<string, string>;
};

type PointsReward = {
  id: string;
  name?: string;
  points?: number | null;
  assetUrl?: string | null;
};
